import React, { useState } from "react";
import firebase from "firebase/compat/app";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  TextField,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

const PasswordSettings = ({ user, visible, close, setSubmitted }) => {
  const { t } = useTranslation("account");
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState(null);

  const newPassword = watch("newPassword");

  const onSubmit = async (data) => {
    setLoading(true);
    setFirebaseError(null);

    try {
      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        data.currentPassword
      );
      await user.reauthenticateWithCredential(credential);
      await user.updatePassword(data.newPassword);

      reset();
      setSubmitted(t("passwordSettings.updated"));
      close();
    } catch (error) {
      switch (error.code) {
        case "auth/wrong-password":
        case "auth/invalid-credential":
          setFirebaseError(t("passwordSettings.currentIncorrect"));
          break;
        case "auth/weak-password":
          setFirebaseError(t("passwordSettings.newWeak"));
          break;
        case "auth/requires-recent-login":
          setFirebaseError(t("errors.recentLogin"));
          break;
        default:
          setFirebaseError(t("errors.generic", { message: error.message }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setFirebaseError(null);
    close();
  };

  return (
    <Dialog open={visible} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{t("passwordSettings.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            {t("passwordSettings.intro")}
          </DialogContentText>

          <Controller
            name="currentPassword"
            control={control}
            rules={{ required: t("passwordSettings.currentRequired") }}
            render={({ field }) => (
              <TextField
                label={t("passwordSettings.currentLabel")}
                type="password"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.currentPassword}
                helperText={errors?.currentPassword?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: t("passwordSettings.newRequired"),
              minLength: {
                value: 6,
                message: t("validation.passwordMin6"),
              },
            }}
            render={({ field }) => (
              <TextField
                label={t("passwordSettings.newLabel")}
                type="password"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.newPassword}
                helperText={errors?.newPassword?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: t("passwordSettings.confirmRequired"),
              validate: (value) =>
                value === newPassword || t("validation.passwordsMismatch"),
            }}
            render={({ field }) => (
              <TextField
                label={t("passwordSettings.confirmLabel")}
                type="password"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.confirmPassword}
                helperText={errors?.confirmPassword?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          {firebaseError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {firebaseError}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.close")}</Button>
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            autoFocus
            variant="contained"
          >
            {t("passwordSettings.submit")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PasswordSettings;
